import type { ReactNode } from 'react';
import { Apex } from '@sweep-io/sweep-design/dist/icons/Apex';
import { ApexTrigger } from '@sweep-io/sweep-design/dist/icons/ApexTrigger';
import { Assign } from '@sweep-io/sweep-design/dist/icons/Assign';
import { At } from '@sweep-io/sweep-design/dist/icons/At';
import { Automations } from '@sweep-io/sweep-design/dist/icons/Automations';
import { Clock } from '@sweep-io/sweep-design/dist/icons/Clock';
import { Copy } from '@sweep-io/sweep-design/dist/icons/Copy';
import { Cube } from '@sweep-io/sweep-design/dist/icons/Cube';
import { Fields } from '@sweep-io/sweep-design/dist/icons/Fields';
import { Flows } from '@sweep-io/sweep-design/dist/icons/Flows';
import { Layout } from '@sweep-io/sweep-design/dist/icons/Layout';
import { PlaybookAlert } from '@sweep-io/sweep-design/dist/icons/PlaybookAlert';
import { RecordType } from '@sweep-io/sweep-design/dist/icons/RecordType';
import { Reporting } from '@sweep-io/sweep-design/dist/icons/Reporting';
import { Shuffle } from '@sweep-io/sweep-design/dist/icons/Shuffle';
import { Stamp } from '@sweep-io/sweep-design/dist/icons/Stamp';
import { Target } from '@sweep-io/sweep-design/dist/icons/Target';
import { ValidationRules } from '@sweep-io/sweep-design/dist/icons/ValidationRules';
import { Wrench } from '@sweep-io/sweep-design/dist/icons/Wrench';
import { colors } from '@sweep-io/sweep-design';
import type { ObjectWithPills } from '../types';
import type { SalesforceItem } from '../../parser/ParserTypes';
import type { DocumentationItem } from '@server/documentation';
import {
  VALIDATION_RULE,
  WORKFLOW_RULE,
  FLOW,
  PROCESS_BUILDER_FLOW,
  APPROVAL_PROCESS,
  APEX_TRIGGER,
  APEX_CLASS,
  REPORT,
  LAYOUT,
  FIELD,
  OBJECT,
  EMAIL_TEMPLATE,
  RECORD_TYPE,
} from '@server/parser';
import type {
  BaseElementProperties,
  BaseFieldProperties,
  ApexClassProperties,
  FlowProperties,
} from '@server/documentation';
import {
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
} from '../../parser/ConfigurationItemTypes';

import type { DocumentationCardContentViewType } from '../building-blocks/documentation-card/types';

export const SWEEP_AUTOMATION_TEMPLATE_TYPES = [
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
];

export const SWEEP_DEDUP_MATCHING_TEMPLATE_TYPES = [SWEEP_DEDUPE, SWEEP_MATCHING];

export const ConfigurationToName: {
  [key: string]: string;
} = {
  [VALIDATION_RULE]: 'Validation Rule',
  [WORKFLOW_RULE]: 'Workflow Rule',
  [FLOW]: 'Flow',
  [PROCESS_BUILDER_FLOW]: 'Process Builder',
  [APPROVAL_PROCESS]: 'Approval Process',
  [APEX_TRIGGER]: 'Apex Trigger',
  [APEX_CLASS]: 'Apex Class',
  [REPORT]: 'Report',
  [LAYOUT]: 'Layout',
  [FIELD]: 'Field',
  [OBJECT]: 'Object',
  [EMAIL_TEMPLATE]: 'Email Template',
  [SWEEP_AUTOMATION]: 'Sweep Automation',
  [SWEEP_ALERT]: 'Sweep Alert',
  [SWEEP_DEDUPE]: 'Sweep Deduplication',
  [SWEEP_MATCHING]: 'Sweep Matching',
  [SWEEP_ASSIGNMENT]: 'Sweep Assignment',
  [SWEEP_SCHEDULED_ASSIGNMENT]: 'Sweep Scheduled Assignment',
  [ROLLUP]: 'Rollup',
  [RECORD_TYPE]: 'Record Type',
  standardFields: 'Standard Fields',
  customFields: 'Custom Fields',
};
const iconColor = colors.grey[700];

export const configurationTypeToIcon: {
  [key: string]: ReactNode;
} = {
  [FIELD]: <Fields color={iconColor} size="medium" />,
  [APEX_TRIGGER]: <ApexTrigger color={iconColor} size="medium" />,
  [APEX_CLASS]: <Apex color={iconColor} size="medium" />,
  [VALIDATION_RULE]: <ValidationRules color={iconColor} size="medium" />,
  [WORKFLOW_RULE]: <Shuffle color={iconColor} size="medium" />,
  [FLOW]: <Flows color={iconColor} size="medium" />,
  [PROCESS_BUILDER_FLOW]: <Wrench color={iconColor} size="medium" />,
  [APPROVAL_PROCESS]: <Stamp color={iconColor} size="medium" />,
  [REPORT]: <Reporting color={iconColor} size="medium" />,
  [LAYOUT]: <Layout color={iconColor} size="medium" />,
  [OBJECT]: <Cube color={iconColor} size="medium" />,
  [EMAIL_TEMPLATE]: <At color={iconColor} size="medium" />,
  [SWEEP_AUTOMATION]: <Automations color={iconColor} size="medium" />,
  [SWEEP_ALERT]: <PlaybookAlert color={iconColor} size="medium" />,
  [SWEEP_DEDUPE]: <Copy color={iconColor} size="medium" />,
  [SWEEP_MATCHING]: <Target color={iconColor} size="medium" />,
  [SWEEP_ASSIGNMENT]: <Assign color={iconColor} size="medium" />,
  [SWEEP_SCHEDULED_ASSIGNMENT]: <Clock color={iconColor} size="medium" />,
  [ROLLUP]: <Fields color={iconColor} size="medium" />,
  [RECORD_TYPE]: <RecordType color={iconColor} size="medium" />,
  standardFields: <Fields color={iconColor} size="medium" />,
  customFields: <Fields color={iconColor} size="medium" />,
};

export type DocumentationCardItem = {
  clearHistory?: boolean;
  item: DocumentationItem;
  contentType: DocumentationCardContentViewType;
};

export type ConfigurationTypesWithIcon =
  | typeof SWEEP_ALERT
  | typeof SWEEP_AUTOMATION
  | typeof SWEEP_ASSIGNMENT
  | typeof SWEEP_SCHEDULED_ASSIGNMENT
  | typeof SWEEP_DEDUPE
  | typeof SWEEP_MATCHING;

export enum UnclassifiedTypes {
  tag = 'tag',
}

export interface ObjectWithPillsMap {
  favorites: ObjectWithPills[];
  standardObjects: ObjectWithPills[];
  cpqObjects: ObjectWithPills[];
  customObjects: ObjectWithPills[];
  platformEvents: ObjectWithPills[];
}

export interface LayoutsByObjectName {
  [objectName: string]: { isLoading: boolean; layouts: SalesforceItem[] };
}

export type ConfigurationTypesWithIds = {
  [configurationKey in string]: string[];
};

export type ConfigurationDependenciesWithParentType = {
  [APEX_CLASS]: (ApexClassProperties & { parentType: string })[];
  [EMAIL_TEMPLATE]: (BaseElementProperties & { parentType: string })[];
  [FIELD]: (BaseFieldProperties & { parentType: string })[];
  [FLOW]: (FlowProperties & { parentType: string })[];
  [PROCESS_BUILDER_FLOW]: (FlowProperties & { parentType: string })[];
};
